import React, { useState, useEffect, useRef } from "react";

interface WorkflowNode {
  id: string;
  type: "process" | "decision" | "action";
  text: string;
  x: number;
  y: number;
}

interface WorkflowConnection {
  source: string;
  target: string;
}

interface WorkflowBuilderProps {
  onMetricsUpdate?: (metrics: {
    automatedTasks: number;
    timeOptimized: number;
    costsReduced: number;
    errorReduction: number;
  }) => void;
}

const WorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  onMetricsUpdate,
}) => {
  const [nodes, setNodes] = useState<WorkflowNode[]>([]);
  const [connections, setConnections] = useState<WorkflowConnection[]>([]);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingStart, setConnectingStart] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<number | null>(
    null
  );
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  const [metrics, setMetrics] = useState({
    automatedTasks: 0,
    timeOptimized: 0,
    costsReduced: 0,
    errorReduction: 0,
  });

  const nodeTemplates = [
    { type: "process", text: "Candidate Search" },
    { type: "action", text: "Auto Outreach" },
    { type: "decision", text: "Follow-up" },
  ];

  useEffect(() => {
    // Update metrics whenever the workflow changes
    const processNodes = nodes.filter((n) => n.type === "process").length;
    const actionNodes = nodes.filter((n) => n.type === "action").length;
    const decisionNodes = nodes.filter((n) => n.type === "decision").length;

    // Calculate metrics based on workflow complexity
    const automatedTasks = processNodes * 2 + actionNodes * 3;
    const timeOptimized = Math.min(
      90,
      connections.length * 5 + nodes.length * 3
    );
    const costsReduced = Math.min(85, timeOptimized * 0.9);
    const errorReduction = Math.min(95, processNodes * 10 + decisionNodes * 15);

    const newMetrics = {
      automatedTasks,
      timeOptimized,
      costsReduced,
      errorReduction,
    };

    // Only update metrics if they've actually changed
    if (JSON.stringify(metrics) !== JSON.stringify(newMetrics)) {
      setMetrics(newMetrics);

      // Update parent component if callback provided
      if (onMetricsUpdate) {
        onMetricsUpdate(newMetrics);
      }
    }
  }, [nodes, connections]);

  // Handle dragging node template
  const handleDragStart = (id: string) => {
    setDraggedItem(id);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Start dragging an existing node
  const handleNodeDragStart = (nodeId: string, e: React.MouseEvent) => {
    if (isConnecting) return;
    e.stopPropagation();

    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return;

    // Calculate the offset from the mouse position to the node's top-left corner
    const nodeElement = e.currentTarget as HTMLElement;
    const rect = nodeElement.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    setDragOffset({ x: offsetX, y: offsetY });
    setIsDraggingNode(true);
    setDraggedNodeId(nodeId);
    setActiveNode(nodeId);

    // Add event listeners for mousemove and mouseup
    document.addEventListener("mousemove", handleNodeDragMove);
    document.addEventListener("mouseup", handleNodeDragEnd);
  };

  // Handle moving a node while dragging
  const handleNodeDragMove = (e: MouseEvent) => {
    if (!isDraggingNode || !draggedNodeId || !canvasRef.current) return;

    const canvasRect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - canvasRect.left - dragOffset.x;
    const y = e.clientY - canvasRect.top - dragOffset.y;

    // Ensure the node stays within canvas boundaries
    const clampedX = Math.max(0, Math.min(x, canvasRect.width - 150));
    const clampedY = Math.max(0, Math.min(y, canvasRect.height - 50));

    // Update the node position
    setNodes((prev) =>
      prev.map((node) =>
        node.id === draggedNodeId ? { ...node, x: clampedX, y: clampedY } : node
      )
    );
  };

  // End dragging a node
  const handleNodeDragEnd = () => {
    setIsDraggingNode(false);
    setDraggedNodeId(null);

    // Remove event listeners
    document.removeEventListener("mousemove", handleNodeDragMove);
    document.removeEventListener("mouseup", handleNodeDragEnd);
  };

  // Handle dropping node template on canvas
  const handleCanvasDrop = (e: React.DragEvent) => {
    e.preventDefault();

    if (!draggedItem) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Find the template being dragged
    const templateIdx = parseInt(draggedItem.split("-")[1]);
    const template = nodeTemplates[templateIdx];

    // Create new node
    const newNode: WorkflowNode = {
      id: `node-${Date.now()}`,
      type: template.type as "process" | "decision" | "action",
      text: template.text,
      x,
      y,
    };

    setNodes([...nodes, newNode]);
    setDraggedItem(null);
  };

  // Handle dropping node on canvas
  const handleCanvasDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Handle node selection
  const handleNodeClick = (nodeId: string, e: React.MouseEvent) => {
    if (isDraggingNode) return;
    e.stopPropagation();

    if (isConnecting) {
      // Complete connection if connecting to a different node
      if (connectingStart && connectingStart !== nodeId) {
        // Check if this connection already exists
        const connectionExists = connections.some(
          (conn) =>
            (conn.source === connectingStart && conn.target === nodeId) ||
            (conn.source === nodeId && conn.target === connectingStart)
        );

        if (!connectionExists) {
          setConnections([
            ...connections,
            { source: connectingStart, target: nodeId },
          ]);
        }
        setIsConnecting(false);
        setConnectingStart(null);
      }
    } else {
      setActiveNode(nodeId === activeNode ? null : nodeId);
      // Deselect any selected connection when selecting a node
      setSelectedConnection(null);
    }
  };

  // Start creating a connection
  const handleStartConnection = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsConnecting(true);
    setConnectingStart(nodeId);
  };

  // Remove a node and its connections
  const handleRemoveNode = (nodeId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    // Remove the node
    setNodes(nodes.filter((node) => node.id !== nodeId));

    // Remove all connections to/from this node
    setConnections(
      connections.filter(
        (conn) => conn.source !== nodeId && conn.target !== nodeId
      )
    );

    setActiveNode(null);
  };

  // Handle connection selection
  const handleConnectionClick = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedConnection(index === selectedConnection ? null : index);
    setActiveNode(null);
  };

  // Remove a connection
  const handleRemoveConnection = () => {
    if (selectedConnection !== null) {
      const newConnections = [...connections];
      newConnections.splice(selectedConnection, 1);
      setConnections(newConnections);
      setSelectedConnection(null);
    }
  };

  // Cancel any ongoing operations when clicking the canvas
  const handleCanvasClick = () => {
    setActiveNode(null);
    setSelectedConnection(null);
    if (isConnecting) {
      setIsConnecting(false);
      setConnectingStart(null);
    }
  };

  // Draw connections between nodes
  const renderConnections = () => {
    return connections.map((conn, idx) => {
      const sourceNode = nodes.find((n) => n.id === conn.source);
      const targetNode = nodes.find((n) => n.id === conn.target);

      if (!sourceNode || !targetNode) return null;

      const sourceX = sourceNode.x + 75; // Middle right of source
      const sourceY = sourceNode.y + 25; // Middle of node
      const targetX = targetNode.x; // Middle left of target
      const targetY = targetNode.y + 25; // Middle of node

      // Create a bezier curve path
      const dx = Math.abs(targetX - sourceX) * 0.5;
      const path = `M ${sourceX} ${sourceY} C ${sourceX + dx} ${sourceY}, ${
        targetX - dx
      } ${targetY}, ${targetX} ${targetY}`;

      const isSelected = selectedConnection === idx;

      return (
        <svg
          key={`conn-${idx}`}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          <path
            d={path}
            stroke={isSelected ? "#ff9500" : "#8b5cf6"}
            strokeWidth={isSelected ? "3" : "2"}
            fill="none"
            markerEnd="url(#arrowhead)"
            style={{ pointerEvents: "auto", cursor: "pointer" }}
            onClick={(e) => handleConnectionClick(idx, e)}
          />
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon
                points="0 0, 10 3.5, 0 7"
                fill={isSelected ? "#ff9500" : "#8b5cf6"}
              />
            </marker>
          </defs>
        </svg>
      );
    });
  };

  // Generate node background color based on type
  const getNodeColor = (type: string) => {
    switch (type) {
      case "process":
        return "bg-purple-600";
      case "action":
        return "bg-blue-500";
      case "decision":
        return "bg-indigo-500";
      default:
        return "bg-gray-500";
    }
  };

  // Auto-arrange nodes in a grid layout
  const handleAutoArrange = () => {
    if (nodes.length === 0) return;

    const GRID_SPACING_X = 180;
    const GRID_SPACING_Y = 100;
    const NODES_PER_ROW = 3;

    const newNodes = [...nodes].map((node, index) => {
      const row = Math.floor(index / NODES_PER_ROW);
      const col = index % NODES_PER_ROW;

      return {
        ...node,
        x: 20 + col * GRID_SPACING_X,
        y: 20 + row * GRID_SPACING_Y,
      };
    });

    setNodes(newNodes);
  };

  return (
    <div className="bg-[#0A0A1B] p-4 md:p-6 rounded-xl max-w-4xl mx-auto">
      <h2 className="text-xl md:text-2xl font-bold text-center text-purple-400 mb-4 md:mb-6">
        Interactive Workflow Builder
      </h2>
      <p className="text-gray-400 text-center mb-4 md:mb-6 px-2 text-sm md:text-base">
        Design your own automated workflows with drag-and-drop and see how your
        work gets simplified.
      </p>

      {/* Drag templates */}
      <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-4 md:mb-6">
        {nodeTemplates.map((template, idx) => (
          <div
            key={`template-${idx}`}
            draggable
            onDragStart={() => handleDragStart(`template-${idx}`)}
            onDragEnd={handleDragEnd}
            className={`${getNodeColor(
              template.type
            )} px-3 py-1.5 md:px-4 md:py-2 rounded-lg cursor-move text-white text-sm md:text-base shadow-md 
              transition-transform hover:scale-105`}
          >
            {template.text}
          </div>
        ))}
      </div>

      {/* Controls row */}
      <div className="flex justify-between items-center mb-3">
        <div className="text-xs text-gray-400">
          {nodes.length} {nodes.length === 1 ? "node" : "nodes"} |{" "}
          {connections.length}{" "}
          {connections.length === 1 ? "connection" : "connections"}
        </div>
        <button
          onClick={handleAutoArrange}
          className="text-sm bg-gray-800 hover:bg-gray-700 text-purple-300 px-2 py-1 rounded transition-colors"
          disabled={nodes.length === 0}
        >
          Auto-Arrange
        </button>
      </div>

      {/* Workflow canvas */}
      <div
        ref={canvasRef}
        id="workflow-container"
        className="relative bg-gray-900/50 border border-gray-800 rounded-lg mb-4 md:mb-6 overflow-hidden"
        style={{ height: "300px" }}
        onDragOver={handleCanvasDragOver}
        onDrop={handleCanvasDrop}
        onClick={handleCanvasClick}
      >
        {/* Render workflow nodes */}
        {nodes.map((node) => (
          <div
            key={node.id}
            className={`absolute ${getNodeColor(
              node.type
            )} px-2 py-1.5 md:px-4 md:py-2 rounded-lg cursor-grab shadow-md
              ${activeNode === node.id ? "ring-2 ring-white" : ""}
              ${
                isDraggingNode && draggedNodeId === node.id
                  ? "cursor-grabbing opacity-80 ring-2 ring-purple-300 z-20"
                  : ""
              }`}
            style={{
              left: `${node.x}px`,
              top: `${node.y}px`,
              zIndex: activeNode === node.id ? 10 : 1,
              minWidth: "150px",
              textAlign: "center",
              transition:
                isDraggingNode && draggedNodeId === node.id
                  ? "none"
                  : "box-shadow 0.2s, opacity 0.2s",
            }}
            onClick={(e) => handleNodeClick(node.id, e)}
            onMouseDown={(e) => handleNodeDragStart(node.id, e)}
          >
            {node.text}

            {/* Connection handle */}
            <div
              className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-purple-400 rounded-full border-2 border-white cursor-pointer
                hover:bg-purple-300 transition-colors"
              title="Connect to another node"
              onClick={(e) => handleStartConnection(node.id, e)}
            />

            {/* Remove node button - only visible when node is active */}
            {activeNode === node.id && (
              <div
                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center cursor-pointer
                  hover:bg-red-600 transition-colors z-30"
                title="Remove node"
                onClick={(e) => handleRemoveNode(node.id, e)}
              >
                <span className="text-white text-xs font-bold">×</span>
              </div>
            )}
          </div>
        ))}

        {/* Render connections */}
        {renderConnections()}

        {/* Instructions if no nodes */}
        {nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm md:text-base">
            Drag and drop workflow elements here to build your automation
          </div>
        )}

        {/* Visual indicator when connecting nodes */}
        {isConnecting && connectingStart && (
          <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center pointer-events-none">
            <div className="bg-black bg-opacity-50 text-white px-4 py-2 rounded text-sm md:text-base">
              Click on another node to connect
            </div>
          </div>
        )}

        {/* Remove connection button */}
        {selectedConnection !== null && (
          <div
            className="absolute bottom-4 right-4 bg-red-500 text-white px-2 py-1.5 md:px-3 md:py-2 rounded-lg cursor-pointer
              hover:bg-red-600 transition-colors text-sm"
            onClick={handleRemoveConnection}
          >
            Remove Connection
          </div>
        )}
      </div>

      {/* Metrics display */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-gray-800/50 p-3 rounded-lg">
          <h4 className="text-xs md:text-sm text-gray-400 mb-1">
            Tasks Automated
          </h4>
          <p className="text-lg md:text-xl font-bold text-purple-400">
            {metrics.automatedTasks}
          </p>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg">
          <h4 className="text-xs md:text-sm text-gray-400 mb-1">Time Saved</h4>
          <p className="text-lg md:text-xl font-bold text-green-400">
            {metrics.timeOptimized}%
          </p>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg">
          <h4 className="text-xs md:text-sm text-gray-400 mb-1">
            Efficiency Gain
          </h4>
          <p className="text-lg md:text-xl font-bold text-blue-400">
            {metrics.costsReduced}%
          </p>
        </div>

        <div className="bg-gray-800/50 p-3 rounded-lg">
          <h4 className="text-xs md:text-sm text-gray-400 mb-1">
            Error Reduction
          </h4>
          <p className="text-lg md:text-xl font-bold text-purple-400">
            {metrics.errorReduction}%
          </p>
        </div>
      </div>

      {/* Instructions */}
      <div className="text-xs md:text-sm text-gray-400 bg-gray-800/30 p-3 md:p-4 rounded-lg">
        <p className="mb-2">
          <strong>Instructions:</strong>
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Drag templates to the canvas to create nodes</li>
          <li>Click and drag nodes to reposition them</li>
          <li>Click the circle on a node's right side to create connections</li>
          <li>
            Click on a connection line to select it, then use the remove button
          </li>
          <li>Click on a node to select it, then click the X to remove it</li>
          <li>Use the Auto-Arrange button to organize your nodes</li>
        </ul>
      </div>
    </div>
  );
};

export default WorkflowBuilder;
